/**
 * Daily Challenges Component
 * Displays today's challenges with progress tracking
 */

import React, { useState, useEffect } from 'react';
import { CHALLENGES, CHALLENGE_CATEGORIES, ChallengeId, Challenge } from '../types/challenges';
import { getTodaysChallenges, getChallengeProgressPercent, formatChallengeProgress, getTimeUntilChallengeReset } from '../utils/challenges';

interface DailyChallengesProps {
  userChallenges?: Partial<Record<ChallengeId, { progress: number; completed: boolean }>>;
  onClaimReward?: (challengeId: ChallengeId) => void;
  compact?: boolean;
}

const DailyChallenges: React.FC<DailyChallengesProps> = ({
  userChallenges = {},
  onClaimReward,
  compact = false,
}) => {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0 });
  const [todayChallenges, setTodayChallenges] = useState<ChallengeId[]>([]);

  useEffect(() => {
    // Get today's challenges
    const { challenges } = getTodaysChallenges();
    setTodayChallenges(challenges);

    // Update time remaining
    const updateTime = () => {
      setTimeRemaining(getTimeUntilChallengeReset());
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const getChallengeState = (challengeId: ChallengeId) => {
    return userChallenges[challengeId] || { progress: 0, completed: false };
  };

  const renderChallenge = (challengeId: ChallengeId) => {
    const challenge = CHALLENGES[challengeId];
    const state = getChallengeState(challengeId);
    const progress = getChallengeProgressPercent(challengeId, state.progress);
    const category = CHALLENGE_CATEGORIES[challenge.category];

    if (compact) {
      return (
        <div
          key={challengeId}
          className={`flex items-center gap-2 p-2 rounded-lg ${
            state.completed ? 'bg-green-50 dark:bg-green-900/20' : 'bg-slate-50 dark:bg-slate-800'
          }`}
        >
          <span className="text-lg">{challenge.icon}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{challenge.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {formatChallengeProgress(challengeId, state.progress)}
            </p>
          </div>
          {state.completed && (
            <span className="text-green-500 text-sm">✓</span>
          )}
        </div>
      );
    }

    return (
      <div
        key={challengeId}
        className={`p-4 rounded-xl border-2 transition-all ${
          state.completed
            ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{challenge.icon}</span>
            <div>
              <h4 className="font-semibold text-slate-900 dark:text-slate-100">
                {challenge.name}
              </h4>
              <span className={`text-xs font-medium ${category.color}`}>
                {category.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            <span className="text-sm font-semibold">+{challenge.xpReward}</span>
            <span className="text-xs">XP</span>
          </div>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
          {challenge.description}
        </p>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
            <span>Progress</span>
            <span>{formatChallengeProgress(challengeId, state.progress)}</span>
          </div>
          <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                state.completed
                  ? 'bg-green-500'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Claim Button */}
        {state.completed && !state.completed && (
          <button
            onClick={() => onClaimReward?.(challengeId)}
            className="w-full py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            Claim Reward
          </button>
        )}
        {state.completed && (
          <div className="w-full py-2 text-center bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-semibold rounded-lg">
            ✓ Completed
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Daily Challenges
        </h3>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Resets in {timeRemaining.hours}h {timeRemaining.minutes}m
        </div>
      </div>

      {/* Challenge Grid */}
      <div className={compact ? 'space-y-2' : 'grid grid-cols-1 gap-4'}>
        {todayChallenges.map(renderChallenge)}
      </div>
    </div>
  );
};

export default DailyChallenges;
